// import ReCAPTCHA from "react-google-recaptcha"
import React, { useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import css from "./Footer.scss"
import GAEvents from "./GAEvents"
import { isPwaUrl } from "@lib/urlMatchUtility"

import PrimaryButton from "@components/Button/Button"
import { fetchData } from "./reducer"
import FooterAccordion from "@components/Experiments/vsat/FooterAccordion/FooterAccordion"
import ImageLoader from "@components/ImageLoader/ImageLoader"
import ConditionalLink from "@wrappers/ConditionalLink/ConditionalLink"

const StatsSection = ({ header, stats, statsOrder }) => {
    const orderedStats = statsOrder.map((stat, idx) => {
        const statData = stats[stat]

        return (
            <div
                key={idx}
                className={`col-8 flex alignCenter justifyBetween   gap-2 pTop-20 pBottom-16 pRight-20 pLeft-20 curved-lg ${css.borderColor}`}
            >
                <div className="flexColumn alignStart gap-2">
                    <div className="headingMediumBold">{statData?.value}</div>
                    <div className="headingExtraSmallRegular">{statData?.stat?.web}</div>
                </div>
                <div className="shrink-0 flex alignCenter">
                    <ImageLoader
                        src={statData?.icon || "https://placehold.co/48x48"}
                        width="48px"
                        height="48px"
                    />
                </div>
            </div>
        )
    })

    return (
        <>
            <div className="flexColumn gap-32 mTop-40">
                {header && (
                    <div className="col-24 headingSmallBold  flex alignCenter justifyCenter">{header}</div>
                )}
                <div className="flex gap-8">{orderedStats}</div>
            </div>
        </>
    )
}

const OrderedLinks = ({ sectionsOrder, sectionsLinks }) => {
    return sectionsOrder.map((section) => {
        const sectionLinks = sectionsLinks[section]
        return (
            <div key={sectionLinks.header} className="col-4">
                <div className="headingExtraSmallBold">{sectionLinks.header}</div>
                <div className="marginTop-16  flexColumn gap-12">
                    {sectionLinks.links.map((link) => {
                        return (
                            <ConditionalLink
                                key={link.url}
                                to={link.url}
                                isExternal={!isPwaUrl(link.url)}
                                className="noAnchorColor"
                                onClick={() => GAEvents.onClick(link.url, sectionLinks.header)}
                            >
                                <div className="bodyLargeRegular textTertiary">{link.text}</div>
                            </ConditionalLink>
                        )
                    })}
                </div>
            </div>
        )
    })
}

const SocialMediaLinks = ({ socialData, signUpData, isLoggedIn }) => {
    const location = useLocation()
    return (
        <div className="col-6">
            <div className="headingExtraSmallBold">{socialData.main_header}</div>
            <div className="bodyLargeRegular textTertiary  marginTop-16">{socialData.text}</div>
            <div className="flex marginTop-12 gap-20">
                {socialData.links?.map((link) => {
                    return (
                        <div key={link.url} className={css.socialMediaLink}>
                            <ConditionalLink
                                to={link.url}
                                isExternal={!isPwaUrl(link.url)}
                                openInNewTab
                                onClick={() => GAEvents.onClick(link.name, socialData.text)}
                            >
                                <ImageLoader src={link.icon} width="24px" height="24px" />
                            </ConditionalLink>
                        </div>
                    )
                })}
            </div>
            {signUpData && !isLoggedIn && (
                <div>
                    <div className="bodyRegular textSecondary">{signUpData.text}</div>
                    <div className={css.sendLinkButton}>
                        <ConditionalLink
                            to={`/?signup=true&followup=${location.pathname}`}
                            isExternal={!isPwaUrl(`/?signup=true&followup=${location.pathname}`)}
                        >
                            <PrimaryButton label={signUpData.cta} />
                        </ConditionalLink>
                    </div>
                </div>
            )}
        </div>
    )
}

const DownloadAppLinks = ({ downloadData, pageName }) => {
    const redirectionType = (name) => {
        if (name === "google_play") {
            return "google_play_store"
        }
        if (name === "app_store") {
            return "apple_app_store"
        }
        return null
    }
    return (
        <div className={`col-5 flexColumn ${css.marginLeft}`}>
            <div className="headingExtraSmallBold">{downloadData.text}</div>
            {downloadData.data?.map((item) => (
                <ConditionalLink
                    key={item.url}
                    to={item.url}
                    isExternal={!isPwaUrl(item.url)}
                    openInNewTab
                    onClick={() => {
                        GAEvents.onDownloadAppLink(item.name)
                        GAEvents.onDownloadAppLinkMixPanel({
                            page_name: pageName,
                            cta_type: "fixed",
                            redirection_type: redirectionType(item.name),
                        })
                    }}
                >
                    <ImageLoader src={item.icon} width={146} height={40} className="marginTop-16" />
                </ConditionalLink>
            ))}
        </div>
    )
}

const LinksSection = ({ sectionsLinks, sectionsOrder, socialData, signUpData, downloadData, pageName }) => {
    const isLoggedIn = useSelector((state) => state.shellReducer)
    return (
        <>
            <div className="flex gap-8 marginTop-48">
                {sectionsOrder && sectionsLinks && (
                    <OrderedLinks sectionsOrder={sectionsOrder} sectionsLinks={sectionsLinks} />
                )}
                {socialData && signUpData && (
                    <SocialMediaLinks
                        socialData={socialData}
                        signUpData={signUpData}
                        isLoggedIn={isLoggedIn}
                    />
                )}
                {downloadData && <DownloadAppLinks downloadData={downloadData} pageName={pageName} />}
            </div>
        </>
    )
}

const TrustSection = ({ data }) => {
    return (
        <>
            <div className="marginTop-96 flex justifyBetween alignStart">
                {data.map((item) => (
                    <div key={item.header} className="col-8 flex gap-16">
                        <ImageLoader src={item.image} alt={item.header} height="64px" width="64px" />
                        <div>
                            <div className="headingExtraSmallBold textMain">{item.header}</div>
                            <div className="bodyLargeRegular textTertiary mTop-4">{item.text}</div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    )
}

const LegitSection = ({ data }) => {
    return (
        <>
            <div className="flexColumn alignCenter marginTop-64 marginBottom-24">
                <div className="col-8 flexColumn alignCenter justifyCenter">
                    <div className="flex gap-32 alignCenter justifyCenter">
                        {data.icons?.map((icon) => (
                            <ConditionalLink to={icon.url} key={icon.url} isExternal={!isPwaUrl(icon.url)}>
                                <ImageLoader src={icon.icon} alt={icon.name} height="96px" />
                            </ConditionalLink>
                        ))}
                    </div>
                    <div className="bodyLargeRegular textTertiary  marginTop-24 flex alignCenter justifyCenter">
                        {data.text}
                    </div>
                </div>
            </div>
        </>
    )
}

const KnowMoreSection = ({ header, data }) => {
    return (
        <div className="mTop-48">
            <FooterAccordion title={header}>
                {data.map((section, index) => (
                    <div key={index} className="marginTop-12">
                        <div className="l5SemiBold">{section.header}</div>
                        <div className="bodyRegular">{section.description}</div>
                    </div>
                ))}
            </FooterAccordion>
        </div>
    )
}

const CopyrightSection = ({ text }) => {
    return (
        <>
            <div className="flex justifyCenter bodyLargeRegular textMain marginTop-16 paddingBoth-16-0">
                {text}
            </div>
        </>
    )
}

const PaymentPartners = ({ paymentPartners }) => {
    return (
        <div className="flex alignCenter gap-20 marginTop-24 pBottom-48">
            <span className="bodyLargeBold textMain">{paymentPartners.text}</span>

            {paymentPartners.data.map((icon, index) => {
                return <ImageLoader key={index} src={icon.icon} alt={icon.name} height={34} width={34} />
            })}
        </div>
    )
}

function Footer() {
    const dispatch = useDispatch()
    const { footerData } = useSelector((state) => state.footerReducer)
    const { pageName } = useSelector((state) => state.shellReducer)

    const {
        headers,
        platformStatsOrder,
        statsData,
        domainsSectionsOrder,
        sectionsLinks,
        connectData,
        downloadApp,
        trustSection,
        legitScript,
        signupData,
        knowMoreSection,
        copyrightText,
        paymentPartners,
    } = footerData

    useEffect(() => {
        dispatch(fetchData())
    }, [])

    return (
        <div className="page-container container-fluid">
            <div>
                {headers && statsData && platformStatsOrder && (
                    <StatsSection
                        header={headers.header2}
                        stats={statsData}
                        statsOrder={platformStatsOrder.desktop}
                    />
                )}
            </div>

            <div>
                {sectionsLinks && domainsSectionsOrder && connectData && signupData && downloadApp && (
                    <LinksSection
                        sectionsLinks={sectionsLinks}
                        sectionsOrder={domainsSectionsOrder.pharmacy}
                        socialData={connectData}
                        signUpData={signupData}
                        downloadData={downloadApp}
                        pageName={pageName}
                    />
                )}
                {trustSection && <TrustSection data={trustSection} />}
                {legitScript && <LegitSection data={legitScript} />}
                {knowMoreSection && (
                    <KnowMoreSection header={knowMoreSection.header} data={knowMoreSection.data} />
                )}
                {copyrightText && <CopyrightSection text={copyrightText} />}
                {paymentPartners && <PaymentPartners paymentPartners={paymentPartners} />}
            </div>
        </div>
    )
}

Footer.serverSideFunction = () => {
    return Promise.resolve()
}

Footer.setFooterData = () => {
    return Promise.resolve()
}

export default Footer
